import type { MentoringDiscount } from '@algoacademy/backend/modules/payment/discount-pricing'
import { ORDER_TYPE } from '@algoacademy/backend/modules/payment/endpoints/mutations/stripe-checkout/types'
import type { GetStudentMentoringStateOutputDTO } from '@algoacademy/backend/modules/payment/endpoints/queries/get-student-mentoring-state/output.dto'
import type {
  PaymentBillingCadence,
  PaymentMentoringType
} from '@algoacademy/backend/modules/payment/enums'
import { MENTORING_TYPE_BY_ORDER } from './constants'
import { isSameDiscount } from './discount-comparison'
import type { MentoringOfferProduct } from './mentoring-terms'

export type ChangeMentoringConfirmationMode =
  | 'schedule'
  | 'replace'
  | 'takeBack'
  | 'unchangedInForce'
  | 'unchangedScheduled'

type ProductTerms = {
  mentoringType: PaymentMentoringType
  billingCadence: PaymentBillingCadence
  hoursWithMentor: number | undefined
}

export function readProductTerms(mentoringProduct: MentoringOfferProduct): ProductTerms {
  const mentoringType = MENTORING_TYPE_BY_ORDER[mentoringProduct.order]
  const billingCadence = mentoringProduct.subscription
  // Hours belong to extended mentoring only - anywhere else the backend refuses them.
  const hoursWithMentor =
    mentoringProduct.order === ORDER_TYPE.INDIVIDUAL_MENTORING
      ? mentoringProduct.hoursWithMentor
      : undefined

  return { mentoringType, billingCadence, hoursWithMentor }
}

type ChangeConfirmationModeArgs = {
  current: NonNullable<GetStudentMentoringStateOutputDTO['current']> | null
  scheduledChange: GetStudentMentoringStateOutputDTO['scheduledChange']
  productTerms: ProductTerms
  chosenDiscount: MentoringDiscount | null
}

export function readChangeConfirmationMode({
  current,
  scheduledChange,
  productTerms,
  chosenDiscount
}: ChangeConfirmationModeArgs): ChangeMentoringConfirmationMode {
  const { mentoringType, billingCadence, hoursWithMentor } = productTerms

  const isTermsInForce =
    current !== null &&
    current.mentoringType === mentoringType &&
    current.billingCadence === billingCadence &&
    current.hoursWithMentor === (hoursWithMentor ?? null) &&
    // The discount is part of the terms: the same plan for less money is a real
    // change, and the confirmation must not promise that nothing happens.
    isSameDiscount(chosenDiscount, current.discount)

  const isTermsAlreadyScheduled =
    scheduledChange !== null &&
    scheduledChange !== undefined &&
    scheduledChange.mentoringType === mentoringType &&
    scheduledChange.billingCadence === billingCadence &&
    scheduledChange.hoursWithMentor === (hoursWithMentor ?? null) &&
    isSameDiscount(chosenDiscount, scheduledChange.discount)

  if (scheduledChange !== null && scheduledChange !== undefined) {
    if (isTermsInForce) {
      return 'takeBack'
    }
    if (isTermsAlreadyScheduled) {
      return 'unchangedScheduled'
    }
    return 'replace'
  }

  if (isTermsInForce) {
    return 'unchangedInForce'
  }

  return 'schedule'
}
